import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Download, Upload } from 'lucide-react'
import { api, apiErrorMessage } from '../../lib/api'
import { downloadFile } from '../../lib/download'
import { COUNTRIES, COUNTRY_LABEL, type Country } from '@emag/shared'
import { ClientSelect } from '../../components/ClientSelect'
import { useClientSelection } from '../../stores/clientSelection'
import { fmtMoney, fmtDate } from '../../lib/format'

interface PreviewNote {
  docd: string
  docn: string
  condb: string
  concr: string
  suma: number
  docdec: string
}
interface Summary {
  count: number
  total: number
  positive: number
  negative: number
  matched?: number
  pendingPayout?: number
  noOrderRef?: number
  vouchersExcluded?: number
  /** Doar la compensarea retururilor. */
  pairs?: number
  unmatched?: number
}
interface PreviewResp {
  summary: Summary
  notes: PreviewNote[]
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-b4l-border bg-b4l-bg-soft p-3">
      <div className="text-[15px] font-semibold text-b4l-text">{value}</div>
      <div className="text-[11px] text-b4l-text-muted">{label}</div>
    </div>
  )
}

function ExportCard({
  title,
  subtitle,
  previewPath,
  downloadPath,
  filename,
  clientId,
  country,
  extraStats,
}: {
  title: string
  subtitle: string
  previewPath: string
  downloadPath: string
  filename: string
  clientId: number | null
  /** Sold-2025 se genereaza fara tara (spec 7.3): sursa lui n-are dimensiunea piata. */
  country?: string
  extraStats?: (s: Summary) => { label: string; value: string }[]
}) {
  const [downloading, setDownloading] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: [previewPath, clientId, country],
    queryFn: async () =>
      (await api.get<PreviewResp>(previewPath, { params: { clientId, ...(country ? { country } : {}) } })).data,
    enabled: clientId != null,
  })

  async function download() {
    if (clientId == null) return
    setDownloading(true)
    try {
      const q = country ? `?clientId=${clientId}&country=${country}` : `?clientId=${clientId}`
      await downloadFile(`${downloadPath}${q}`, filename)
      toast.success('Fișier descărcat')
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setDownloading(false)
    }
  }

  const s = data?.summary
  const notes = data?.notes ?? []

  return (
    <div className="rounded-xl border border-b4l-border bg-white p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-b4l-text">{title}</h2>
          <p className="text-[11px] text-b4l-text-dim">{subtitle}</p>
        </div>
        <button
          onClick={download}
          disabled={clientId == null || downloading || (s?.count ?? 0) === 0}
          className="flex items-center gap-1.5 rounded-md bg-b4l-green px-3 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60"
        >
          <Download size={14} />
          {downloading ? 'Se descarcă…' : 'Descarcă XLSX'}
        </button>
      </div>

      {clientId == null && <p className="text-[13px] text-b4l-text-dim">Alege un client.</p>}
      {clientId != null && isLoading && <p className="text-[13px] text-b4l-text-dim">Se calculează…</p>}

      {s && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Note" value={String(s.count)} />
            <Stat label="Pozitive" value={String(s.positive)} />
            <Stat label="Storno" value={String(s.negative)} />
            <Stat label="Total net" value={fmtMoney(s.total)} />
            {extraStats?.(s).map((x) => <Stat key={x.label} label={x.label} value={x.value} />)}
          </div>

          {s.count === 0 ? (
            <p className="text-[13px] text-b4l-text-dim">Nimic de generat (sincronizează/încarcă datele întâi).</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-b4l-border">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-b4l-border text-left text-b4l-text-muted">
                    <th className="px-3 py-2 font-medium">Data</th>
                    <th className="px-3 py-2 font-medium">Doc</th>
                    <th className="px-3 py-2 font-medium">Debit</th>
                    <th className="px-3 py-2 font-medium">Credit</th>
                    <th className="px-3 py-2 text-right font-medium">Sumă</th>
                  </tr>
                </thead>
                <tbody>
                  {notes.slice(0, 30).map((n, i) => (
                    <tr key={i} className="border-b border-b4l-border last:border-0">
                      <td className="px-3 py-1.5">{fmtDate(n.docd)}</td>
                      <td className="px-3 py-1.5">{n.docdec}</td>
                      <td className="px-3 py-1.5">{n.condb}</td>
                      <td className="px-3 py-1.5">{n.concr}</td>
                      <td className={`px-3 py-1.5 text-right ${n.suma < 0 ? 'text-b4l-red' : ''}`}>{fmtMoney(n.suma)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {s.count > 30 && (
                <div className="border-t border-b4l-border px-3 py-2 text-[11px] text-b4l-text-dim">
                  … și încă {s.count - 30} note (fișierul le conține pe toate).
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface ScraperJob {
  id: number
  country: string
  status: string
  message: string | null
  otpTarget: string | null
  filesFound: number
  fractions: number
  vouchers: number
}

/**
 * Descarcarea desfasuratoarelor direct din Seller Center. Fluxul e intrerupt de codul de
 * verificare: serverul nu are ecran, deci il cere aici si il transmite mai departe.
 */
function ScraperCard({ clientId, country }: { clientId: number | null; country: string }) {
  const qc = useQueryClient()
  const [otp, setOtp] = useState('')
  const [starting, setStarting] = useState(false)

  const { data: job } = useQuery({
    queryKey: ['scraper-latest', clientId],
    queryFn: async () => (await api.get<{ job: ScraperJob | null }>(`/scraper/${clientId}/latest`)).data.job,
    enabled: clientId != null,
    // Cat timp ruleaza, reimprospatam des ca sa prindem momentul in care cere codul.
    refetchInterval: (q) => {
      const st = (q.state.data as ScraperJob | null | undefined)?.status
      return st === 'running' || st === 'awaiting_otp' ? 2500 : false
    },
  })

  const active = job?.status === 'running' || job?.status === 'awaiting_otp'

  async function start() {
    if (clientId == null) return
    setStarting(true)
    try {
      await api.post(`/scraper/${clientId}/start`, { country })
      toast.success('Scraper pornit')
      void qc.invalidateQueries({ queryKey: ['scraper-latest', clientId] })
    } catch (err) {
      toast.error(apiErrorMessage(err), { duration: 8000 })
    } finally {
      setStarting(false)
    }
  }

  async function cancel() {
    if (clientId == null || !job) return
    try {
      await api.post(`/scraper/${clientId}/jobs/${job.id}/cancel`)
      toast.success('Rulare anulată')
      void qc.invalidateQueries({ queryKey: ['scraper-latest', clientId] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  async function sendOtp() {
    if (clientId == null || !job) return
    try {
      await api.post(`/scraper/${clientId}/jobs/${job.id}/otp`, { code: otp.trim() })
      setOtp('')
      toast.success('Cod trimis')
      void qc.invalidateQueries({ queryKey: ['scraper-latest', clientId] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <div className="rounded-xl border border-dashed border-b4l-border bg-b4l-bg-soft p-5">
      <h2 className="mb-1 text-[14px] font-semibold text-b4l-text">Descarcă desfășurătoarele din Seller Center</h2>
      <p className="mb-3 text-[11px] leading-snug text-b4l-text-dim">
        Se autentifică pe eMAG cu datele de portal ale clientului, descarcă desfășurătoarele noi și le importă
        direct. Sesiunea rămâne salvată criptat, deci codul de verificare se cere rar. Fișierele deja importate
        se sar.
      </p>

      <button
        onClick={() => void start()}
        disabled={clientId == null || starting || active}
        className="flex items-center gap-1.5 rounded-md border border-b4l-border bg-white px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-white disabled:opacity-60"
      >
        <Upload size={14} />
        {active ? 'Rulează…' : starting ? 'Pornesc…' : `Descarcă pentru ${country}`}
      </button>
      {active && (
        <button
          onClick={() => void cancel()}
          className="ml-2 rounded-md border border-b4l-border px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-white"
        >
          Anulează rularea
        </button>
      )}

      {job && (
        <div className="mt-3 rounded-md border border-b4l-border bg-white px-3 py-2">
          <div className="text-[12px] text-b4l-text">
            <span
              className={
                job.status === 'failed'
                  ? 'text-red-600'
                  : job.status === 'done'
                    ? 'text-b4l-green'
                    : 'text-b4l-text-muted'
              }
            >
              {job.status === 'awaiting_otp'
                ? 'Așteaptă codul de verificare'
                : job.status === 'running'
                  ? 'În lucru'
                  : job.status === 'done'
                    ? 'Gata'
                    : 'Eșuat'}
            </span>
            {job.message ? ` — ${job.message}` : ''}
          </div>
          {job.status === 'done' && (job.fractions > 0 || job.vouchers > 0) && (
            <div className="mt-1 text-[11px] text-b4l-text-dim">
              {job.filesFound} fișiere · {job.fractions} fracții · {job.vouchers} vouchere
            </div>
          )}

          {job.status === 'awaiting_otp' && (
            <div className="mt-2">
              {job.otpTarget && <p className="mb-1 text-[11px] text-b4l-text-dim">{job.otpTarget}</p>}
              <div className="flex items-center gap-2">
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="codul primit"
                  className="w-36 rounded-md border border-b4l-border px-2 py-1 text-[13px] outline-none focus:border-b4l-green"
                />
                <button
                  onClick={() => void sendOtp()}
                  disabled={otp.trim().length < 4}
                  className="rounded-md bg-b4l-green px-3 py-1 text-[13px] text-white disabled:opacity-40"
                >
                  Trimite
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function UploadCard({ clientId }: { clientId: number | null }) {
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  async function upload(files: FileList | null) {
    if (clientId == null || !files || files.length === 0) return
    const fd = new FormData()
    Array.from(files).forEach((f) => fd.append('files', f))
    setUploading(true)
    try {
      const res = await api.post(`/upload/${clientId}/desfasuratoare`, fd)
      const r = res.data as {
        dpFiles: number
        dvFiles: number
        unknownFiles: number
        fractionsImported: number
        voucherOrdersImported: number
      }
      const parts = []
      if (r.dpFiles) parts.push(`${r.fractionsImported} fracții DP`)
      if (r.dvFiles) parts.push(`${r.voucherOrdersImported} comenzi voucher`)
      toast.success(
        (parts.join(' + ') || 'Nimic recunoscut') + (r.unknownFiles ? ` (${r.unknownFiles} ignorate)` : ''),
      )
      void qc.invalidateQueries({ queryKey: ['/ea-export/income/preview'] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="rounded-xl border border-dashed border-b4l-border bg-b4l-bg-soft p-5">
      <h2 className="mb-1 text-[14px] font-semibold text-b4l-text">Import desfășurătoare de plată (DP)</h2>
      <p className="mb-3 text-[11px] text-b4l-text-dim">
        Încarcă fișierele XLSX din Seller Center (Financiar → Desfășurătoare): <b>DP</b> (payout, sursa încasărilor) și
        <b> voucher</b> (comenzile pe voucher se exclud din încasări). Se detectează automat tipul.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        multiple
        onChange={(e) => upload(e.target.files)}
        disabled={clientId == null || uploading}
        className="hidden"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={clientId == null || uploading}
        className="flex items-center gap-1.5 rounded-md border border-b4l-border bg-white px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-white disabled:opacity-60"
      >
        <Upload size={14} />
        {uploading ? 'Se încarcă…' : 'Alege fișiere DP (.xlsx)'}
      </button>
    </div>
  )
}

/**
 * Aceeasi grila „Datorii si Creante" din EA, doua laturi. La payout avem nevoie de
 * amandoua: facturile de client de incasat si facturile de comision de compensat.
 */

/**
 * Cursurile se incarca EXPLICIT, nu in timpul generarii: altfel o cadere de retea la
 * BNR oprea exportul, iar interogarile automate ale paginii o declansau singure.
 */
function CursuriBnrCard() {
  const qc = useQueryClient()
  const [loading, setLoading] = useState(false)
  const [progres, setProgres] = useState('')
  const { data } = useQuery({
    queryKey: ['bnr-rates'],
    queryFn: async () => (await api.get<{ years: { year: number; rows: number }[] }>('/ea-export/bnr/rates')).data,
  })
  const ani = data?.years ?? []
  const anCurent = new Date().getFullYear()
  const necesari = Array.from({ length: anCurent - 2020 }, (_, i) => 2021 + i)
  const lipsa = necesari.filter((y) => !ani.some((a) => a.year === y && a.rows > 0))

  /**
   * Un an pe cerere, nu toti odata: sase ani intr-o singura cerere treceau de `timeout`-ul
   * clientului (15 s), iar cererea era abandonata desi serverul continua sa lucreze — pe
   * ecran aparea doar „A aparut o eroare" (Alexandru, 25.09.2026).
   *
   * Asa, fiecare cerere e scurta, se vede cat s-a facut, iar un an cazut nu pierde restul.
   */
  async function incarca() {
    setLoading(true)
    const deFacut = lipsa.length ? lipsa : necesari
    let adaugate = 0
    const esuati: number[] = []
    try {
      for (const [i, an] of deFacut.entries()) {
        setProgres(`${an} (${i + 1}/${deFacut.length})`)
        try {
          // 3 minute: descarcarea de la BNR plus scrierea a ~8.000 de randuri.
          const r = await api.post('/ea-export/bnr/rates', { years: [an] }, { timeout: 180_000 })
          adaugate += (r.data.loaded as { year: number; added: number }[]).reduce((s, x) => s + x.added, 0)
        } catch (err) {
          esuati.push(an)
          toast.error(`${an}: ${apiErrorMessage(err)}`, { duration: 10000 })
        }
        void qc.invalidateQueries({ queryKey: ['bnr-rates'] })
      }
      if (adaugate) toast.success(`${adaugate.toLocaleString('ro-RO')} cursuri noi încărcate`)
      else if (!esuati.length) toast.success('Cursurile erau deja încărcate')
    } finally {
      setProgres('')
      setLoading(false)
    }
  }

  return (
    <div className="rounded-xl border border-b4l-border bg-white p-5">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-b4l-text">Cursuri BNR</h2>
          <p className="text-[11px] text-b4l-text-dim">
            Necesare doar pentru facturile în valută (BG, HU). Cursul unui document e cel comunicat în ziua
            bancară precedentă — cel de vineri acoperă sâmbăta, duminica și luni.
          </p>
        </div>
        <button
          onClick={() => void incarca()}
          disabled={loading}
          className="shrink-0 rounded-md border border-b4l-border px-3 py-1.5 text-[13px] font-medium text-b4l-text hover:bg-b4l-bg-soft disabled:opacity-60"
        >
          {loading ? `Se încarcă ${progres}…` : lipsa.length ? `Încarcă ${lipsa.length} ani` : 'Reîncarcă'}
        </button>
      </div>
      {ani.length === 0 ? (
        <p className="text-[12px] text-b4l-red">Niciun curs încărcat — exportul BG/HU va eșua până apeși butonul.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {ani.map((a) => (
            <span key={a.year} className="rounded-md bg-b4l-bg-soft px-2 py-0.5 text-[11px] text-b4l-text-muted">
              {a.year}: {a.rows.toLocaleString('ro-RO')}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function ReceivablesCard({ clientId, kind = 'creanta' }: { clientId: number | null; kind?: 'creanta' | 'datorie' }) {
  const datorii = kind === 'datorie'
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  async function upload(files: FileList | null) {
    if (clientId == null || !files || files.length === 0) return
    const fd = new FormData()
    Array.from(files).forEach((f) => fd.append('files', f))
    setUploading(true)
    try {
      const res = await api.post(`/upload/${clientId}/${datorii ? 'ea-datorii' : 'ea-receivables'}`, fd)
      const r = res.data as {
        imported: number
        updated: number
        perCountry: Record<string, number>
        skipped: { reason: string; count: number }[]
      }
      const tari = Object.entries(r.perCountry)
        .map(([c, n]) => `${c}: ${n}`)
        .join(' · ')
      const sarite = r.skipped.map((x) => `${x.count} ${x.reason}`).join(' · ')
      toast.success(
        `${r.imported} noi, ${r.updated} actualizate${tari ? `\n${tari}` : ''}${sarite ? `\nsărite: ${sarite}` : ''}`,
        { duration: 8000 },
      )
      void qc.invalidateQueries({ queryKey: ['imports', clientId] })
      void qc.invalidateQueries({ queryKey: ['/ea-export/income/preview'] })
    } catch (err) {
      toast.error(apiErrorMessage(err), { duration: 10000 })
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="rounded-xl border border-dashed border-b4l-border bg-b4l-bg-soft p-5">
      <h2 className="mb-1 text-[14px] font-semibold text-b4l-text">
        {datorii
          ? 'Import facturi de plătit (Datorii, ExpertAccounts)'
          : 'Import „Datorii și creanțe" (ExpertAccounts)'}
      </h2>
      <p className="mb-3 text-[11px] leading-snug text-b4l-text-dim">
        {datorii ? (
          <>
            Documentele deschise pe <b>furnizori (401)</b> — facturile de comision rămase de compensat la payout.
            În EA: <b>Meniu → Operațiuni → Datorii și Creanțe</b>, bifezi <b>Datorii</b>, lași „Numai cu sold",
            apeși <b>Încarcă arbore</b> și exporți grila. Facturile de furnizor n-au număr de comandă, deci aici
            rândurile fără <code>docns</code> se păstrează; țara se deduce din monedă.
          </>
        ) : (
          <>
            Necesar doar pentru clienții care <b>facturează din ExpertAccounts</b>, nu din eMAG — acolo facturile
            de vânzare nu există în eMAG, deci sursa e exportul de grilă din EA. Se importă doar rândurile care au
            număr de comandă; țara se deduce din monedă.
          </>
        )}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx"
        multiple
        onChange={(e) => upload(e.target.files)}
        disabled={clientId == null || uploading}
        className="hidden"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={clientId == null || uploading}
        className="flex items-center gap-1.5 rounded-md border border-b4l-border bg-white px-3 py-1.5 text-[13px] text-b4l-text-muted hover:bg-white disabled:opacity-60"
      >
        <Upload size={14} />
        {uploading
          ? 'Se încarcă…'
          : datorii
            ? 'Alege fișier cu Datorii (.xlsx)'
            : 'Alege fișier „Datorii și creanțe" (.xlsx)'}
      </button>
    </div>
  )
}

interface ImportBatchRow {
  id: string
  country: string
  kind: string
  fileName: string
  activeRows: number
  createdAt: string
}

function ImportsCard({ clientId }: { clientId: number | null }) {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['imports', clientId],
    queryFn: async () =>
      (await api.get<{ imports: ImportBatchRow[] }>(`/upload/${clientId}/imports`)).data.imports,
    enabled: clientId != null,
  })

  async function cancel(b: ImportBatchRow) {
    if (!window.confirm(`Anulezi importul „${b.fileName}"? Se șterg ${b.activeRows} rânduri.`)) return
    try {
      const res = await api.delete(`/upload/${clientId}/imports/${b.id}`)
      toast.success(`Șterse ${(res.data as { deleted: number }).deleted} rânduri`)
      void qc.invalidateQueries({ queryKey: ['imports', clientId] })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  const rows = data ?? []
  if (!rows.length) return null

  return (
    <div className="rounded-xl border border-b4l-border bg-white p-5">
      <h2 className="mb-1 text-[14px] font-semibold text-b4l-text">Încărcări recente</h2>
      <p className="mb-3 text-[11px] leading-snug text-b4l-text-dim">
        Singura cale de corecție după un fișier încărcat greșit. Un lot cu <b>0 rânduri active</b> a fost preluat
        de un import mai nou — nu mai are ce șterge.
      </p>
      <div className="space-y-1.5">
        {rows.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-3 rounded-md border border-b4l-border px-3 py-2">
            <div className="min-w-0">
              <div className="truncate text-[13px] text-b4l-text">{b.fileName}</div>
              <div className="text-[11px] text-b4l-text-dim">
                {b.country} · {new Date(b.createdAt).toLocaleString('ro-RO')} · {b.activeRows} rânduri active
              </div>
            </div>
            <button
              onClick={() => void cancel(b)}
              disabled={b.activeRows === 0}
              className="shrink-0 rounded-md border border-b4l-border px-2.5 py-1 text-[12px] text-b4l-text-muted hover:bg-b4l-bg-soft disabled:opacity-40"
            >
              Anulează
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export function GeneratorEaPage() {
  const clientId = useClientSelection((s) => s.selectedClientId)
  const [country, setCountry] = useState<Country>('RO')

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-b4l-text">Generator import EA</h1>
        <ClientSelect />
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-b4l-border bg-white px-4 py-3">
          <span className="text-[13px] text-b4l-text-muted">Țara pentru care se generează:</span>
          {COUNTRIES.map((c) => (
            <button
              key={c}
              onClick={() => setCountry(c)}
              className={
                country === c
                  ? 'rounded-md bg-b4l-green px-3 py-1 text-[13px] text-white'
                  : 'rounded-md border border-b4l-border px-3 py-1 text-[13px] text-b4l-text-muted hover:bg-b4l-bg-soft'
              }
            >
              {COUNTRY_LABEL[c]}
            </button>
          ))}
          <span className="text-[11px] text-b4l-text-dim">
            Piețele în altă monedă decât leul sunt blocate până în etapa 2.
          </span>
        </div>

        <ScraperCard clientId={clientId} country={country} />
        <UploadCard clientId={clientId} />
        <CursuriBnrCard />
        <ReceivablesCard clientId={clientId} />
        <ReceivablesCard clientId={clientId} kind="datorie" />
        <ImportsCard clientId={clientId} />

        <ExportCard
          title="Încasări (5125.eMag = 4111.client)"
          subtitle="Din facturile de vânzare, la data payout-ului (DP). Storno = restituire, sumă negativă."
          previewPath="/ea-export/income/preview"
          downloadPath="/ea-export/income/download"
          filename={`IMPORT_incasari_client${clientId}_${country}.xlsx`}
          clientId={clientId}
          country={country}
          extraStats={(s) => [
            { label: 'Pending payout', value: String(s.pendingPayout ?? 0) },
            { label: 'Voucher excluse', value: String(s.vouchersExcluded ?? 0) },
            { label: 'Fără comandă', value: String(s.noOrderRef ?? 0) },
          ]}
        />

        <ExportCard
          title="Plăți comision Dante (401.Dante = 5125.eMag)"
          subtitle="Din facturile de cumpărare. O notă per factură; storno negativ."
          previewPath="/ea-export/dante-payments/preview"
          downloadPath="/ea-export/dante-payments/download"
          filename={`IMPORT_plati_dante_client${clientId}_${country}.xlsx`}
          clientId={clientId}
          country={country}
        />

        <ExportCard
          title="Compensare retururi (factură + storno deschise)"
          subtitle="Perechi de aceeași valoare pe același partener: două note la data stornoului, +/−, care se anulează pe contul de tranzit. Net zero, fără flux de bani."
          previewPath="/ea-export/returns/preview"
          downloadPath="/ea-export/returns/download"
          filename={`IMPORT_compensari_retur_client${clientId}_${country}.xlsx`}
          clientId={clientId}
          country={country}
          extraStats={(s) => [
            { label: 'Perechi', value: String(s.pairs ?? 0) },
            { label: 'Fără pereche', value: String(s.unmatched ?? 0) },
          ]}
        />

        <ExportCard
          title="Înregistrare facturi servicii eMAG (628 = 401, 4426 = 401)"
          subtitle="Facturile de comision, voucher și livrare, ca note de înregistrare. Cele românești intră oricum prin e-Factura — fișierul e util la import unde nu intră singure, sau ca verificare."
          previewPath="/ea-export/supplier-invoices/preview"
          downloadPath="/ea-export/supplier-invoices/download"
          filename={`IMPORT_facturi_servicii_client${clientId}_${country}.xlsx`}
          clientId={clientId}
          country={country}
        />

        <ExportCard
          title="Solduri de deschidere 2025 (31-Ian-2026)"
          subtitle="Închide soldurile reportate 2025 (prefix 2025GSW) prin 5125.eMag = 4111.client, la 31-Ian."
          previewPath="/ea-export/sold-2025/preview"
          downloadPath="/ea-export/sold-2025/download"
          filename={`IMPORT_sold2025_client${clientId}.xlsx`}
          clientId={clientId}
        />
      </div>
    </div>
  )
}
